import React, {useState, useEffect, useContext} from 'react';
import axios from "@common/axios";
import useDrivePicker from 'react-google-drive-picker';
import {Form, Input, Select, Divider, Checkbox, Button, Table, Tooltip, Space, Upload, Progress, Alert, Popconfirm} from 'antd';
import { UploadOutlined, DownloadOutlined, PlayCircleOutlined, ClockCircleOutlined, LoadingOutlined, CloseCircleOutlined, CheckCircleOutlined, FileDoneOutlined } from '@ant-design/icons';
import {ContentContext} from "@context/ContentContext";
import {UserContext} from "@context/UserContext";
import useInterval from "@hooks/useInterval";
import CircularLoader from "@common/CircularLoader";

const ContentImport = () => {
    const {content} = useContext(ContentContext);
    const {user} = useContext(UserContext);
    const [contentImportForm] = Form.useForm();

    // File Tracking State
    const [driveFile, setDriveFile] = useState(null);
    const [localFile, setLocalFile] = useState(null);

    const [syllabusGroupId, setSyllabusGroupId] = useState();
    const [syllabusId, setSyllabusId] = useState();
    const [message, setMessage] = useState(null);

    const [contentImport, setContentImport] = useState();
    const [contentImports, setContentImports] = useState([]);
    const [openPicker, authResponse] = useDrivePicker();
    const [token, setToken] = useState();
    const [isLoading, setLoading] = useState(false);
    const [isImporting, setImporting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStatus, setUploadStatus] = useState('');

    const handleOpenPicker = () => {
        let accessToken = undefined;
        if(token) {
            if((new Date().getTime()) < token.expiryTime) {
                accessToken = token.accessToken;
            }
        }

        openPicker({
            clientId: "870966665234-gen30igve29sabqlinp3jf08q32gpd5k.apps.googleusercontent.com",
            developerKey: "AIzaSyCFZgE5yZ5-hgTQ-DgRxO2OOzGGSgJ3Jw8",
            viewMimeTypes: "application/vnd.google-apps.folder,application/zip,application/x-zip-compressed",
            showUploadView: false,
            showUploadFolders: false,
            supportDrives: true,
            multiselect: false,
            token: accessToken,
            callbackFunction: (data) => {
                if(data.action === 'picked') {
                    if(data.docs && data.docs.length > 0) {
                        const file = data.docs[0];
                        setDriveFile(file);
                        setLocalFile(null); // Clear local file if Drive is selected
                        setMessage(null); // Clear existing messages
                    }
                }
            }
        })
    }

    // Router function to handle either Google Drive or Local S3 Uploads
    const handleValidateContent = () => {
        setMessage(null);

        if (!syllabusId) {
            setMessage({ type: 'warning', text: 'Please select a Course.' });
            return;
        }

        if (driveFile) {
            validateDriveContent();
        } else if (localFile) {
            validateLocalContent();
        } else {
            setMessage({ type: 'warning', text: 'Please select a ZIP file from Google Drive or your local computer.' });
        }
    }

    // Legacy Google Drive Upload Flow
    const validateDriveContent = () => {
        setLoading(true);
        axios.post('/web/ui/content/import/validateContent', null, {
            params: { contentFileId: driveFile.id, syllabusId: syllabusId }
        }).then(response => {
            setContentImport(response.data);
            findContentImports();
        }).catch(error => {
            console.error('Error validating Drive content', error);
            setMessage({ type: 'error', text: 'Error validating Drive content. Please try again.' });
        }).finally(() => {
            setLoading(false);
            setDriveFile(null);
        });
    }

    const validateLocalContent = async () => {
        setLoading(true);
        setUploadProgress(0);
        setUploadStatus('Requesting secure upload URL...');
        setMessage(null);

        try {
            // STEP 1: Get the Presigned URL
            const urlResponse = await axios.get('/web/ui/content/import/requestUploadUrl', {
                params: {
                    syllabusId: syllabusId,
                    fileName: localFile.name,
                    contentType: localFile.type || 'application/zip'
                }
            });
            const { presignedUrl, s3Key } = urlResponse.data;

            // STEP 2: Upload directly to AWS S3
            setUploadStatus('Uploading to cloud storage...');
            await axios.put(presignedUrl, localFile, {
                headers: {
                    'Content-Type': localFile.type || 'application/zip'
                },
                onUploadProgress: (progressEvent) => {
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    setUploadProgress(percentCompleted);
                }
            });

            // STEP 3: Notify Spring Boot to trigger the Validation Batch Job
            setUploadStatus('Finalizing and starting Validation Job...');
            await axios.post('/web/ui/content/import/triggerValidation', null, {
                params: {
                    s3Key: s3Key,
                    fileName: localFile.name,
                    contentType: localFile.type || 'application/zip',
                    syllabusId: syllabusId
                }
            });

            setUploadStatus('Validation triggered successfully!');
            findContentImports();
            setLocalFile(null); // Reset file selection
        } catch (error) {
            console.error('Upload failed:', error);
            setMessage({ type: 'error', text: 'Error during local file upload or validation trigger.' });
        } finally {
            setLoading(false);
            setTimeout(() => { setUploadProgress(0); setUploadStatus(''); }, 5000);
        }
    }

    const handleTriggerImport = async (contentImportId) => {
        try {
            setImporting(true);
            await axios.post('/web/ui/content/import/triggerImport', null, {
                params: { contentImportId: contentImportId }
            });
            setMessage({ type: 'success', text: 'Import successfully triggered!' });
            findContentImports(); // Refresh table state
        } catch (error) {
            console.error('Error triggering import:', error);
            setMessage({ type: 'error', text: 'Failed to trigger the import process.' });
        }
    }

    const handleSyllabusGroupChange = (newSyllabusGroupId) => {
        setSyllabusGroupId(newSyllabusGroupId);

        const availableCourses = content.syllabusList?.filter(
            syllabus => syllabus.syllabusGroupId === newSyllabusGroupId
        );

        if (availableCourses && availableCourses.length > 0) {
            setSyllabusId(availableCourses[0].syllabusId);
        } else {
            setSyllabusId(null);
        }
    }

    const handleSyllabusChange = (syllabusId) => {
        setSyllabusId(syllabusId);
    }

    const findContentImports = () => {
        axios.get('/web/ui/content/import/findContentImports').then((response) => {
            setContentImports(response.data);
        }).catch(function (error) {
            console.error('Error fetching imports', error);
        });
    };

    const reloadInProgressContentImports = () => {
        if(contentImports && contentImports.some(contentImport => contentImport.inProgress || contentImport.pending)) {
            findContentImports();
        }
    };

    const handleValidationFileDownload = async (contentImportId, validationFileName) => {
        if(!contentImportId || !validationFileName) return;

        try {
            const response = await axios.get('/web/ui/content/import/downloadContentValidationReport', {
                params: {contentImportId: contentImportId},
                responseType: 'blob',
            });

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = validationFileName;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    const handleImportFileDownload = async (contentImportId, validationFileName) => {
        if(!contentImportId || !validationFileName) return;

        try {
            const response = await axios.get('/web/ui/content/import/downloadContentImportReport', {
                params: {contentImportId: contentImportId},
                responseType: 'blob',
            });

            const blob = new Blob([response.data]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = validationFileName;
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
        }
    };

    useEffect(() => {
        findContentImports();
        setSyllabusGroupId(content.currentSyllabus ? content.currentSyllabus.syllabusGroupId : user.syllabusGroupId);
        setSyllabusId(content.currentSyllabus ? content.currentSyllabus.syllabusId : user.syllabusId);
        const contentImportToken = sessionStorage.getItem('contentImportToken');
        if(contentImportToken) {
            setToken(JSON.parse(contentImportToken));
        }
    }, []);

    useEffect(() => {
        if(authResponse) {
            const accessToken = authResponse['access_token'];
            const expiryTime = (authResponse['expires_in'] * 1000 ) + new Date().getTime();

            const contentImportToken  = {accessToken: accessToken, expiryTime: expiryTime};
            sessionStorage.setItem('contentImportToken', JSON.stringify(contentImportToken));
            setToken(contentImportToken);
        }
    }, [authResponse]);

    useInterval(reloadInProgressContentImports, 5000);

    const columns = [
        {
            title: 'Date',
            dataIndex: 'createDate',
            key: 'createDate',
            render: createDate => user.dateTimeFormat.format(createDate)
        },
        {
            title: 'User',
            dataIndex: 'updateUserFullName',
            key: 'updateUserFullName',
        },
        {
            title: 'File name',
            dataIndex: 'contentFileName',
            key: 'contentFileName',
        },
        {
            title: 'Course',
            dataIndex: 'syllabusName',
            key: 'syllabusName',
        },
        {
            title: 'File size',
            dataIndex: 'contentFileSize',
            key: 'contentFileSize',
        },
        {
            title: 'File Count',
            dataIndex: 'fileCount',
            key: 'fileCount',
        },
        {
            title: 'Error Count',
            dataIndex: 'validationErrorCount',
            key: 'validationErrorCount',
        },
        {
            title: 'Status',
            dataIndex: 'importStatus', // Keep dataIndex to use 'text' for our icon logic
            key: 'importStatus',
            render: (text, record) => (
                <Space>
                    {(record.importStatus === 'PENDING_IMPORT' || record.importStatus === 'PENDING_VALIDATION') && (
                        <ClockCircleOutlined style={{ color: '#faad14' }} />
                    )}
                    {record.inProgress && (
                        <LoadingOutlined style={{ color: '#1890ff' }} />
                    )}
                    {(record.importStatus === 'FAILED' || record.importStatus ===  'VALIDATION_FAILED') && (
                        <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
                    )}
                    {record.importStatus === 'VALIDATED' && (
                        <CheckCircleOutlined style={{ color: '#1890ff' }} />
                    )}
                    {(record.importStatus === 'IMPORTED' || record.importStatus === 'PROCESSED' || record.importStatus === 'COMPLETED') && (
                        <FileDoneOutlined style={{ color: '#52c41a' }} />
                    )}
                    <span>{record.importStatusDescription}</span>
                </Space>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, locContentImport) => {
                return (
                    <Space orientation="vertical" size="small" align="center">
                        {locContentImport.validationFileName && (
                            <Button
                                type="default"
                                icon={<DownloadOutlined />}
                                onClick={() => handleValidationFileDownload(locContentImport.contentImportId, locContentImport.validationFileName)}
                            >
                                Validation CSV
                            </Button>
                        )}
                        {locContentImport.importFileName && (
                            <Button
                                type="default"
                                icon={<DownloadOutlined />}
                                onClick={() => handleImportFileDownload(locContentImport.contentImportId, locContentImport.importFileName)}
                            >
                                Import CSV
                            </Button>
                        )}
                        {/* Only show Import button if there are no validation errors */}
                        {locContentImport.importStatus === 'VALIDATED' && locContentImport.validationErrorCount === 0 && (
                            <Popconfirm
                                title="Confirm Content Import"
                                description="Are you sure you want to trigger this import? This action has a high impact and will modify live content."
                                onConfirm={() => handleTriggerImport(locContentImport.contentImportId)}
                                okText="Yes, Import"
                                cancelText="Cancel"
                                placement="left"
                            >
                                <Button
                                    disabled={isImporting}
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                >
                                    Import
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                );
            },
        }
    ];

    return (
        <div>
            <h2>Import Content</h2>

            {/* Added Ant Design Alert Component for general messages */}
            {message && (
                <Alert
                    message={message.text}
                    type={message.type}
                    showIcon
                    closable
                    onClose={() => setMessage(null)}
                    style={{ marginBottom: '16px', maxWidth: 600 }}
                />
            )}

            <Form
                name="contentImportForm"
                form={contentImportForm}
                autoComplete="off"
                layout="horizontal"
            >
                <Form.Item label="Series" style={{marginBottom: '8px'}} required>
                    <Select
                        style={{ width: 600 }}
                        value={syllabusGroupId}
                        onChange={handleSyllabusGroupChange}
                        options={content.syllabusGroupList?.map(syllabusGroup => ({value: syllabusGroup.syllabusGroupId, label: syllabusGroup.syllabusGroupName}))}
                    />
                </Form.Item>
                <Form.Item label="Course" style={{marginBottom: '8px'}} required>
                    <Select
                        style={{ width: 600 }}
                        value={syllabusId}
                        onChange={handleSyllabusChange}
                        options={content.syllabusList?.filter(syllabus => syllabus.syllabusGroupId == syllabusGroupId).map(syllabus => ({value: syllabus.syllabusId, label: syllabus.syllabusName}))}
                    />
                </Form.Item>

                <Form.Item label="Select content ZIP file" style={{marginBottom: '8px'}} required>
                    <Space size="large">
                        <Button icon={<UploadOutlined />} onClick={handleOpenPicker}>Select from Google Drive</Button>
                        <span> OR </span>
                        <Upload
                            accept=".zip"
                            beforeUpload={(file) => {
                                setLocalFile(file);
                                setDriveFile(null); // Clear Drive file if local is selected
                                setMessage(null); // Clear previous messages
                                return false; // Prevent automatic upload by Ant Design
                            }}
                            fileList={localFile ? [localFile] : []}
                            onRemove={() => setLocalFile(null)}
                            maxCount={1}
                        >
                            <Button icon={<UploadOutlined />}>Upload Local File</Button>
                        </Upload>
                    </Space>
                </Form.Item>

                {(driveFile || localFile) && (
                    <Form.Item label="Validate content file">
                        <Space>
                            <Button type="primary" onClick={handleValidateContent} disabled={isLoading}>
                                Validate
                            </Button>
                            <span style={{marginLeft: '8px'}}>
                                {driveFile ? driveFile.name : localFile.name}
                            </span>
                            <CircularLoader pageCentered={false} displayInElement={true} visible={isLoading}/>
                        </Space>
                    </Form.Item>
                )}

                {/* Real-time Upload Progress Bar & Status Alert */}
                {uploadProgress > 0 && uploadProgress <= 100 && (
                    <Form.Item style={{ maxWidth: 600 }}>
                        <Progress percent={uploadProgress} status={uploadProgress === 100 ? "success" : "active"} />
                        {uploadStatus && (
                            <Alert
                                title={uploadStatus}
                                type={uploadProgress === 100 ? "success" : "info"}
                                showIcon
                                style={{ marginTop: '8px' }}
                            />
                        )}
                    </Form.Item>
                )}
            </Form>

            {contentImports !== undefined && contentImports.length > 0 &&
                <div>
                    <h2>Recent Imports</h2>
                    <Table dataSource={contentImports} columns={columns} rowKey="contentImportId"/>
                </div>
            }
        </div>
    );
};

export default ContentImport;